import { motion, useReducedMotion } from "framer-motion";
import { Phone, ShieldCheck, HeartPulse, Brain, Users, Leaf, Sun, Star } from "lucide-react";

import Seo from "@/components/site/Seo";
import Footer from "@/components/site/Footer";
import ChatPlaceholder from "@/components/site/ChatPlaceholder";
import ContactForm from "@/components/site/ContactForm";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { WhatsAppIcon } from "@/components/site/icons";
import { WingDivider, Wings } from "@/components/site/Wings";
import MapEmbed from "@/components/site/MapEmbed";
import { waLink, PHONE_DISPLAY, PHONE_TEL, EMAIL, ADDRESS } from "@/lib/contact";

const WA_MSG = "Hola, quiero información sobre acompañamiento en bienestar emocional.";

const areas = [
  { Icon: HeartPulse, title: "Ansiedad y estrés", text: "Herramientas y acompañamiento profesional para recuperar la calma y volver a sentirte en control." },
  { Icon: Brain, title: "Depresión y estado de ánimo", text: "Evaluación psiquiátrica y terapia individual para atender la tristeza persistente y recuperar el sentido de vida." },
  { Icon: Users, title: "Acompañamiento a la familia", text: "Terapia grupal sabatina y familiar individual, porque el bienestar emocional se sostiene en compañía." },
  { Icon: Sun, title: "Un espacio sereno", text: "Un entorno residencial tranquilo en Cuernavaca, pensado para el descanso, la reflexión y la calma." },
];

const trust = [
  { name: "COFEPRIS", text: "Aviso de funcionamiento y responsable sanitario del establecimiento." },
  { name: "NOM-178-SSA1", text: "Norma de operación de establecimientos de atención médica." },
  { name: "CLUNI · OSC", text: "Registro como Organización de la Sociedad Civil ante la Secretaría de Bienestar." },
  { name: "+27 años", text: "De experiencia clínica interdisciplinaria y humanista en Morelos." },
];

const stats = [
  { value: "+27", label: "años de experiencia clínica" },
  { value: "+16", label: "profesionales en el equipo" },
  { value: "86%", label: "índice de satisfacción" },
  { value: "7 días", label: "atención a familias" },
];

const landingSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  name: "CESMA — Bienestar emocional",
  legalName: "Salud Integral Alas Enlazadas A.C.",
  url: "https://www.cesma.com.mx/landing",
  image: "https://www.cesma.com.mx/assets/jardin.webp",
  description:
    "Clínica de bienestar emocional en Cuernavaca, Morelos. Acompañamiento profesional, confidencial y humanista para ansiedad, depresión y estrés.",
  telephone: "+52-777-555-7088",
  email: "contactocesma@gmail.com",
  medicalSpecialty: ["Psychiatric"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "Lisboa 9, Col. Burgos",
    addressLocality: "Cuernavaca",
    addressRegion: "Morelos",
    postalCode: "62580",
    addressCountry: "MX",
  },
};

export default function Landing() {
  const reduce = useReducedMotion();

  return (
    <div className="relative">
      <Seo
        title="Bienestar Emocional en Cuernavaca | CESMA"
        description="Clínica de bienestar emocional en Cuernavaca, Morelos. Acompañamiento profesional, confidencial y humanista para ansiedad, depresión y estrés."
        canonical="https://www.cesma.com.mx/landing"
        robots="noindex, follow"
        jsonLd={[landingSchema]}
      />
      <div className="grain" />

      {/* Header mínimo (autocontenido, sin enlaces a otras páginas) */}
      <header className="sticky top-0 z-50 border-b border-line bg-plaster/85 backdrop-blur-md" data-testid="landing-header">
        <div className="mx-auto flex h-[70px] max-w-7xl items-center justify-between px-5 md:px-8">
          <div className="flex items-center gap-3" data-testid="landing-brand">
            <img src="/assets/logo-mark.png" alt="Logo CESMA" className="h-10 w-auto md:h-11" />
            <span className="leading-none">
              <span className="block font-serif text-[1.45rem] font-semibold tracking-wide text-pine">CESMA</span>
              <span className="mt-[3px] block text-[0.58rem] font-semibold uppercase tracking-[0.14em] text-soft">
                Bienestar emocional
              </span>
            </span>
          </div>
          <a
            href={waLink(WA_MSG)}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 rounded-full bg-marigold px-5 py-3 text-[0.95rem] font-semibold text-[#231402] shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            data-testid="landing-header-whatsapp"
          >
            <WhatsAppIcon className="h-[1.15em] w-[1.15em]" />
            WhatsApp
          </a>
        </div>
      </header>

      <main>
        {/* HERO — banner inmersivo (todas las resoluciones) */}
        <section className="relative flex min-h-[92vh] items-center overflow-hidden">
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <img src="/assets/jardin.webp" alt="" fetchPriority="high" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-pine/90 via-pine/70 to-pine/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-pine/70 via-transparent to-transparent" />
          </div>

          <div className="relative z-10 mx-auto w-full max-w-7xl px-5 py-20 md:px-8 md:py-28">
            <div className="max-w-2xl">
              <motion.div
                initial={{ opacity: 0, y: reduce ? 0 : 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[0.8rem] font-semibold text-white backdrop-blur-sm"
              >
                <span className="h-2 w-2 rounded-full bg-marigold" /> Estamos contigo · Atención 7 días
              </motion.div>
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="eyebrow block text-white/85">
                Cuernavaca, Morelos · Desde 1998
              </motion.span>
              <h1 className="mt-4 font-serif text-[2.5rem] leading-[1.08] text-white [text-shadow:0_1px_22px_rgba(0,0,0,.32)] sm:text-5xl lg:text-[3.7rem]">
                <MaskedLines
                  lines={[
                    "Clínica de bienestar",
                    "emocional en Cuernavaca,",
                    <>
                      con <em className="italic text-marigold">calma</em> y
                    </>,
                    "acompañamiento real.",
                  ]}
                />
              </h1>
              <Reveal delay={0.55} className="mt-6 max-w-[44ch] text-lg text-white/90">
                Un espacio profesional, confidencial y humano para atender la ansiedad, la
                depresión y el estrés, y recuperar el equilibrio emocional.
              </Reveal>
              <Reveal delay={0.65} className="mt-8 flex flex-wrap gap-3">
                <motion.a
                  whileHover={reduce ? {} : { y: -2 }}
                  href={waLink(WA_MSG)}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-marigold px-7 py-3.5 font-semibold text-[#231402] shadow-soft transition-shadow duration-300 hover:shadow-lift"
                  data-testid="landing-hero-whatsapp"
                >
                  <WhatsAppIcon className="h-[1.15em] w-[1.15em]" />
                  Hablar con un especialista
                </motion.a>
                <motion.a
                  whileHover={reduce ? {} : { y: -2 }}
                  href={`tel:${PHONE_TEL}`}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border-[1.5px] border-white px-7 py-3.5 font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-pine"
                  data-testid="landing-hero-phone"
                >
                  <Phone className="h-[1.1em] w-[1.1em]" strokeWidth={1.8} />
                  {PHONE_DISPLAY}
                </motion.a>
              </Reveal>
              <Reveal delay={0.72} className="mt-4 text-[0.92rem] text-white/85">
                Te escuchamos ahora mismo, de forma confidencial y sin compromiso.
              </Reveal>
              <Reveal delay={0.8} className="mt-10 flex flex-wrap gap-x-9 gap-y-5">
                <div className="flex flex-col">
                  <b className="font-serif text-3xl text-white">+27</b>
                  <span className="mt-1 max-w-[15ch] text-[0.8rem] text-white/80">años de experiencia clínica</span>
                </div>
                <div className="flex flex-col">
                  <b className="font-serif text-3xl text-white">100%</b>
                  <span className="mt-1 max-w-[15ch] text-[0.8rem] text-white/80">confidencial y humano</span>
                </div>
                <div className="flex flex-col">
                  <b className="font-serif text-3xl text-white">7 días</b>
                  <span className="mt-1 max-w-[15ch] text-[0.8rem] text-white/80">atención a familias</span>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* EMPATÍA */}
        <section className="border-y border-line bg-warm py-20 md:py-24">
          <Reveal className="mx-auto max-w-3xl px-5 text-center md:px-8">
            <WingDivider className="mb-8" />
            <Leaf className="mx-auto mb-4 h-8 w-8 text-spring" strokeWidth={1.5} />
            <h2 className="font-serif text-[1.8rem] text-ink sm:text-[2.4rem]">
              ¿Tú o un ser querido atraviesan un momento difícil?
            </h2>
            <p className="mx-auto mt-4 max-w-prose text-[1.08rem] text-soft">
              Sentir ansiedad, tristeza persistente o agotamiento no es una debilidad. Pedir
              ayuda a tiempo, con profesionales que te traten con dignidad, marca la diferencia.
              En CESMA te escuchamos sin juicios y te acompañamos paso a paso.
            </p>
          </Reveal>
        </section>

        {/* ÁREAS */}
        <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="max-w-2xl">
            <span className="eyebrow">Cómo acompañamos</span>
            <h2 className="mt-3 font-serif text-[1.9rem] text-ink sm:text-4xl">
              Atención profesional para tu bienestar emocional
            </h2>
            <p className="mt-4 text-[1.08rem] text-soft">
              Un equipo interdisciplinario de psiquiatría, psicología y medicina que atiende a
              la persona completa, en un entorno sereno y confidencial.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
            {areas.map((a, i) => (
              <Reveal
                as="article"
                key={a.title}
                delay={(i % 2) * 0.1}
                whileHover={reduce ? {} : { y: -4 }}
                className="group flex gap-5 rounded-2xl border border-line bg-warm p-7 shadow-soft transition-shadow duration-300 hover:shadow-lift"
                data-testid={`landing-area-${i}`}
              >
                <div className="grid h-12 w-12 flex-none place-items-center rounded-xl bg-spring/15 text-pine transition-colors duration-300 group-hover:bg-spring/25">
                  <a.Icon strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="mb-1.5 font-serif text-xl text-ink">{a.title}</h3>
                  <p className="text-[0.98rem] text-soft">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* STATS */}
        <section className="border-y border-line bg-pine py-16 text-[#EAF0E7] md:py-20">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-10 px-5 text-center md:grid-cols-4 md:px-8">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} className="md:border-l md:border-white/15 md:first:border-l-0">
                <b className="block font-serif text-[2.6rem] leading-none text-white md:text-[3rem]">{s.value}</b>
                <span className="mt-3 block text-[0.92rem] text-[#B9C9BB]">{s.label}</span>
              </Reveal>
            ))}
          </div>
        </section>

        {/* CONFIANZA */}
        <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Reveal className="rounded-[28px] border border-line bg-warm p-8 shadow-soft md:p-12">
            <div className="max-w-2xl">
              <span className="eyebrow">Registros y certificaciones</span>
              <h2 className="mt-3 font-serif text-[1.9rem] text-ink sm:text-4xl">
                Una clínica legalmente reconocida
              </h2>
              <p className="mt-4 text-soft">
                Operamos con los avisos y registros sanitarios que respaldan una atención seria,
                profesional y responsable.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {trust.map((c, i) => (
                <Reveal key={c.name} delay={i * 0.08} className="rounded-2xl border border-line bg-band p-6" data-testid={`landing-cred-${i}`}>
                  <b className="mb-1 block font-serif text-[1.05rem] text-pine">{c.name}</b>
                  <span className="text-[0.85rem] text-soft">{c.text}</span>
                </Reveal>
              ))}
            </div>
            <p className="mt-6 text-[0.9rem] text-soft">
              Operado por <b className="text-pine">Salud Integral Alas Enlazadas A.C.</b> ·
              Contamos con becas y facilidades de pago.
            </p>
          </Reveal>
        </section>

        {/* TESTIMONIO */}
        <section className="border-y border-line bg-warm py-20 md:py-24">
          <Reveal className="mx-auto max-w-3xl px-5 text-center md:px-8">
            <div className="mb-3 flex justify-center gap-1 text-marigold" aria-label="5 de 5 estrellas">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-current" strokeWidth={0} />
              ))}
            </div>
            <blockquote className="font-serif text-[1.4rem] italic leading-[1.4] text-ink sm:text-[2rem]">
              “Encontré un lugar donde me escucharon con respeto y sin prisa. Hoy me siento
              acompañado y con herramientas para seguir adelante.”
            </blockquote>
            <cite className="mt-4 block text-[0.95rem] not-italic text-soft">— Testimonio de un paciente de CESMA</cite>
          </Reveal>
        </section>

        {/* MOMENTO DE CALMA — banda atmosférica */}
        <section aria-hidden="true" className="photo-frame relative h-[36vh] min-h-[260px] overflow-hidden">
          <img src="/assets/atmos-hands.webp" alt="" className="h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-pine/55 via-pine/10 to-transparent" />
          <div className="absolute inset-0 grid place-items-center">
            <Wings className="h-12 w-48 text-warm/90" />
          </div>
        </section>

        {/* CTA / CONTACTO */}
        <section className="bg-gradient-to-b from-plaster to-band">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-2">
            <Reveal>
              <span className="eyebrow">Estamos para escucharte</span>
              <h2 className="mt-3 font-serif text-[1.9rem] text-ink sm:text-4xl">
                Dar el primer paso es lo más difícil
              </h2>
              <p className="mt-4 text-soft">
                Escríbenos o llámanos. Con una llamada podemos orientarte, de forma confidencial
                y sin compromiso, sobre el acompañamiento ideal para ti o tu familia.
              </p>
              <ul className="mt-8 grid gap-5">
                <li className="flex items-start gap-3">
                  <Phone className="mt-0.5 h-[22px] w-[22px] flex-none text-spring" strokeWidth={1.7} />
                  <span>
                    <b className="block">Teléfono y WhatsApp</b>
                    <a href={`tel:${PHONE_TEL}`} className="text-ink hover:text-spring" data-testid="landing-contact-phone">
                      {PHONE_DISPLAY}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-[22px] w-[22px] flex-none text-spring" strokeWidth={1.7} />
                  <span>
                    <b className="block">Correo</b>
                    <a href={`mailto:${EMAIL}`} className="text-ink hover:text-spring" data-testid="landing-contact-email">
                      {EMAIL}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Leaf className="mt-0.5 h-[22px] w-[22px] flex-none text-spring" strokeWidth={1.7} />
                  <span>
                    <b className="block">Ubicación</b>
                    {ADDRESS}
                  </span>
                </li>
              </ul>
              <MapEmbed className="mt-8" />
            </Reveal>
            <Reveal delay={0.1}>
              <ContactForm compliant />
            </Reveal>
          </div>
        </section>
      </main>

      <Footer compliant />
      <ChatPlaceholder />
    </div>
  );
}
